import { useCallback, useMemo, useState } from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Accordion from 'LuxKit/Accordion'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { INTL_DATE_TIME_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { getScheduleChangeDifferences, type ScheduleChangeDifference } from 'lib/flights/getScheduleChangeDifferences'
import { convertMinutesToHoursAndMinutes } from 'lib/flights/flightUtils'
import { parseScheduleChangeDateTime } from 'lib/flights/scheduleChangePresentation'
import { scheduleChangeComparisonMessages, scheduleChangeDetailMessages } from 'lib/flights/scheduleChangeDetailMessages'
import ScheduleChangeJourneyDetails from './ScheduleChangeJourneyDetails'
import type { ScheduleChangeComparisonData, ScheduleChangeFareRulesSelection } from './scheduleChangeDetailsTypes'

interface Props {
  comparison: ScheduleChangeComparisonData
  bookingFareRules?: App.FlightScheduleChange['fareRules']
  onShowFareRules: (selection: ScheduleChangeFareRulesSelection) => void
}

function formatValue(value: string, field: string, intl: IntlShape): string {
  if (field.endsWith('DateTime')) {
    return intlDateFormatter(parseScheduleChangeDateTime(value), INTL_DATE_TIME_FORMAT, intl.locale, 'UTC')
  }
  if (field === 'duration' || field === 'layover') {
    const duration = convertMinutesToHoursAndMinutes(Number(value))
    return intl.formatMessage({
      id: '1BCg0BPZUWY9AZgAKyjK',
      defaultMessage: '{hours}h {minutes}m',
      description: 'Elapsed duration shown as a value in the flight schedule change comparison summary',
    }, { hours: duration.hours, minutes: duration.minutes })
  }
  return value
}

function formatDifference(change: ScheduleChangeDifference, intl: IntlShape): string {
  if (change.kind === 'stops') {
    const previousAirports = change.previous.filter((airport): airport is string => Boolean(airport))
    const updatedAirports = change.updated.filter((airport): airport is string => Boolean(airport))
    const values = {
      previousCount: change.previousCount,
      updatedCount: change.updatedCount,
      previousAirports: intl.formatList(previousAirports),
      updatedAirports: intl.formatList(updatedAirports),
    }
    if (previousAirports.length !== change.previousCount || updatedAirports.length !== change.updatedCount) {
      return intl.formatMessage({
        id: 'Zd8gg4vn-4HNQnTg7yF_',
        defaultMessage: 'Stops: now {updatedCount, plural, =0 {direct} one {# stop} other {# stops}}, previously {previousCount, plural, =0 {direct} one {# stop} other {# stops}}.',
        description: 'Stop count comparison when some airport codes are unavailable in the schedule change summary',
      }, values)
    }
    if (change.previousCount === 0) {
      return intl.formatMessage({
        id: 'fttODUm8VVhthwlBpZf-',
        defaultMessage: 'Stops: now {updatedCount, plural, one {# stop} other {# stops}} via {updatedAirports}, previously direct.',
        description: 'Summary of new stopovers added to a previously direct flight after a schedule change',
      }, values)
    }
    if (change.updatedCount === 0) {
      return intl.formatMessage({
        id: 'KnY6BoYyWWlRkiBFBb8J',
        defaultMessage: 'Stops: now direct, previously {previousCount, plural, one {# stop} other {# stops}} via {previousAirports}.',
        description: 'Summary of a flight becoming direct after its previous stopovers were removed',
      }, values)
    }
    return intl.formatMessage({
      id: 'HowNLN6MES6d7D9WBHb5',
      defaultMessage: 'Stops: now {updatedCount, plural, one {# stop} other {# stops}} via {updatedAirports}, previously {previousCount, plural, one {# stop} other {# stops}} via {previousAirports}.',
      description: 'Summary comparing the ordered stopover airports in the previous and updated flight itineraries',
    }, values)
  }
  if (change.kind === 'details') return intl.formatMessage(scheduleChangeDetailMessages[change.field], { route: change.route })
  return intl.formatMessage(scheduleChangeDetailMessages[change.field], {
    route: change.route,
    previous: formatValue(change.previous, change.field, intl),
    updated: formatValue(change.updated, change.field, intl),
  })
}

function ScheduleChangeComparison({ comparison, bookingFareRules, onShowFareRules }: Props) {
  const intl = useIntl()
  const [activeTab, setActiveTab] = useState(comparison.initialTab)
  const showUpdated = useCallback(() => setActiveTab('updated'), [])
  const showPrevious = useCallback(() => setActiveTab('previous'), [])
  const selectedLeg = comparison[activeTab]
  const differences = useMemo(() => getScheduleChangeDifferences(comparison.previous, comparison.updated), [comparison])
  const summaryItems = useMemo(() => differences.map(change => <BodyText as="li" variant="medium" key={change.id}>{formatDifference(change, intl)}</BodyText>), [differences, intl])
  const summaryHeading = intl.formatMessage({
    id: 'Fa1iCj0RxOKtFRN1wjmu',
    defaultMessage: 'View change summary ({count})',
    description: 'Collapsed accordion heading showing the number of change bullets for one affected flight leg',
  }, { count: differences.length })

  return <VerticalSpacer gap={20}>
    <Heading variant="heading6">{intl.formatMessage({
      id: 'QShBGHnoXW4IvuJ2DndJ',
      defaultMessage: '{departureAirport} ({departureCode}) to {arrivalAirport} ({arrivalCode})',
      description: 'Full selected leg route above the tabs in the flight schedule change comparison modal',
    }, {
      departureAirport: selectedLeg.flights[0]?.departureAirportName ?? selectedLeg.departureCity ?? selectedLeg.departureAirport,
      departureCode: selectedLeg.departureAirport,
      arrivalAirport: selectedLeg.flights[selectedLeg.flights.length - 1]?.arrivalAirportName ?? selectedLeg.arrivalCity ?? selectedLeg.arrivalAirport,
      arrivalCode: selectedLeg.arrivalAirport,
    })}
    </Heading>
    <TabButtonsGroup fit="grow">
      <TabButton size="medium" isActive={activeTab === 'updated'} aria-pressed={activeTab === 'updated'} onClick={showUpdated}><BodyText as="span" variant="medium" wrap="pre-wrap">{intl.formatMessage(scheduleChangeComparisonMessages.updated)}</BodyText></TabButton>
      <TabButton size="medium" isActive={activeTab === 'previous'} aria-pressed={activeTab === 'previous'} onClick={showPrevious}><BodyText as="span" variant="medium" wrap="pre-wrap">{intl.formatMessage(scheduleChangeComparisonMessages.previous)}</BodyText></TabButton>
    </TabButtonsGroup>
    {activeTab === 'updated' && <Pane border="light-foreground" borderRadius="S" padding="0 16">
      <Accordion heading={summaryHeading} hideDivider>
        {differences.length > 0 && <ul>{summaryItems}</ul>}
        {differences.length === 0 && <BodyText variant="medium">{intl.formatMessage({
          id: 'vCTGzmq6itC5NEw3CCoV',
          defaultMessage: 'No comparable changes are available. Review both flight itineraries for details.',
          description: 'Summary fallback when schedule change snapshots contain no reliably comparable differences',
        })}
        </BodyText>}
      </Accordion>
    </Pane>}
    <ScheduleChangeJourneyDetails key={activeTab} leg={selectedLeg} bookingFareRules={activeTab === 'updated' ? bookingFareRules : undefined} onShowFareRules={onShowFareRules} showRoute={false} />
  </VerticalSpacer>
}

export default ScheduleChangeComparison
